import { useState } from 'react';
import {
  AlertTriangle,
  ArrowUpRight,
  Banknote,
  ChevronDown,
  Clock3,
  Download,
  FileSpreadsheet,
  FileText,
  Lightbulb,
  Receipt,
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  Users,
  Utensils,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  AiWidget,
  PeriodComparisonWidgetData,
  ReceiptWidgetData,
  RestaurantPerformanceWidgetData,
  SalesSummaryWidgetData,
  ShopHealth,
  StockAlertsWidgetData,
  TopProductsWidgetData,
} from '@/types/ai';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fcfa(value: number): string {
  return `${new Intl.NumberFormat('fr-FR').format(Math.round(value))} FCFA`;
}

const GRADE_META: Record<ShopHealth['grade'], { label: string; ring: string; pill: string }> = {
  A: { label: 'Excellent', ring: '#76b900', pill: 'bg-[#e2f4d8] text-[#2d6914]' },
  B: { label: 'Bonne santé', ring: '#76b900', pill: 'bg-[#e2f4d8] text-[#2d6914]' },
  C: { label: 'Moyenne', ring: '#F59E0B', pill: 'bg-amber-100 text-amber-800' },
  D: { label: 'Fragile', ring: '#F97316', pill: 'bg-orange-100 text-orange-800' },
  F: { label: 'Critique', ring: '#E11D48', pill: 'bg-rose-100 text-rose-800' },
};

const PERIOD_LABELS: Record<string, string> = {
  today: "Aujourd'hui",
  yesterday: 'Hier',
  this_week: 'Cette semaine',
  last_week: 'Semaine dernière',
  this_month: 'Ce mois',
  last_month: 'Mois dernier',
  last_7d: '7 derniers jours',
  last_30d: '30 derniers jours',
};

function periodLabel(key: string): string {
  return PERIOD_LABELS[key] ?? key;
}

// ---------------------------------------------------------------------------
// Shared card primitives (Soft, ultra-clean design matching Image 3)
// ---------------------------------------------------------------------------

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-[24px] bg-white dark:bg-charcoal-800 p-5 shadow-[0_2px_15px_rgba(0,0,0,0.03)] border-0 ${
        className ?? ''
      }`}
    >
      {children}
    </div>
  );
}

function Kpi({ label, value, trend }: { label: string; value: string; trend?: number }) {
  return (
    <div className="rounded-[20px] bg-white dark:bg-charcoal-800 p-4 shadow-[0_2px_12px_rgba(0,0,0,0.03)] border-0 flex flex-col justify-between">
      <p className="text-xs text-slate-400 dark:text-slate-400 font-medium">{label}</p>
      <p className="mt-2 font-mono text-xl font-bold text-slate-900 dark:text-white leading-tight flex items-baseline gap-1">
        {value}
        {trend !== undefined && trend !== 0 && (
          <span
            className={
              trend > 0
                ? 'inline-flex items-center text-xs font-bold text-[#2d6914]'
                : 'inline-flex items-center text-xs font-bold text-rose-500'
            }
          >
            {trend > 0 ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
          </span>
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

function ShopHealthCard({ data }: { data: ShopHealth }) {
  const score = Math.max(0, Math.min(100, Number(data.score) || 0));
  const meta = GRADE_META[data.grade] ?? GRADE_META.C;
  const r = 34;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - score / 100);
  const m = data.metrics;

  return (
    <Card>
      <div className="flex items-center gap-4">
        <div className="relative size-[86px] shrink-0">
          <svg width="86" height="86" viewBox="0 0 86 86">
            <circle cx="43" cy="43" r={r} fill="none" stroke="#EAECEF" strokeWidth="9" />
            <circle
              cx="43"
              cy="43"
              r={r}
              fill="none"
              stroke={meta.ring}
              strokeWidth="9"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 43 43)"
            />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <span className="font-heading text-xl font-bold text-slate-900 dark:text-white">
              {score}
            </span>
            <span className="text-[9px] text-slate-400">/ 100</span>
          </div>
        </div>
        <div className="min-w-0">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold ${meta.pill}`}
          >
            Note {data.grade} · {meta.label}
          </span>
          <h4 className="mt-1.5 font-heading text-[15px] font-bold text-slate-900 dark:text-white">
            Santé de la boutique
          </h4>
          <p className="text-xs text-slate-400">Sur 6 axes clés</p>
        </div>
      </div>
      {m && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Kpi
            label={m.revenue_growth?.label ?? 'Croissance'}
            value={`${Math.round(Number(m.revenue_growth?.value) || 0)}%`}
            trend={Number(m.revenue_growth?.value) || 0}
          />
          <Kpi
            label={m.average_margin?.label ?? 'Marge'}
            value={`${Math.round(Number(m.average_margin?.value) || 0)}%`}
          />
          <Kpi
            label={m.sales_regularity?.label ?? 'Régularité'}
            value={`${Math.round(Number(m.sales_regularity?.score) || 0)}/100`}
          />
        </div>
      )}
    </Card>
  );
}

function SalesSummaryCard({ data }: { data: SalesSummaryWidgetData }) {
  const total = Number(data.daily_sales?.total) || 0;
  const count = Number(data.daily_sales?.count) || 0;
  const basket = count > 0 ? total / count : 0;
  const topProduct = data.top_products?.[0];

  return (
    <div className="flex flex-col gap-3">
      {/* Main Revenue Card matching Image 3 */}
      <Card className="relative overflow-hidden">
        <h4 className="sr-only">Résumé des ventes</h4>
        <div className="flex items-start justify-between">
          <span className="text-xs font-semibold text-slate-400 dark:text-slate-400">
            Chiffre d'affaires
          </span>
          <div className="size-9 rounded-2xl bg-[#e2f4d8] text-[#2d6914] flex items-center justify-center shrink-0">
            <Banknote className="size-5" />
          </div>
        </div>

        <div className="mt-2 flex items-baseline gap-2">
          <span className="font-mono text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
            {fcfa(total)}
          </span>
        </div>

        <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#e2f4d8] text-[#2d6914] text-xs font-mono font-bold">
          <ArrowUpRight className="size-3.5 stroke-[2.5]" />
          <span>+12% vs hier</span>
        </div>

        {topProduct && (
          <p className="mt-2.5 text-xs text-slate-400 border-t border-slate-100 dark:border-charcoal-700 pt-2">
            Top produit :{' '}
            <span className="font-semibold text-slate-800 dark:text-slate-200">
              {topProduct.product_name}
            </span>{' '}
            ({Number(topProduct.total_quantity) || 0} vendus)
          </p>
        )}
      </Card>

      {/* Two equal columns for Ventes & Panier Moyen */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs font-semibold text-slate-400 leading-tight">
            Nombre de ventes
          </span>
          <span className="font-mono text-2xl font-bold text-slate-900 dark:text-white mt-3">
            {count}
          </span>
        </Card>

        <Card className="p-4 flex flex-col justify-between">
          <span className="text-xs font-semibold text-slate-400 leading-tight">Panier moyen</span>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="font-mono text-lg font-bold text-slate-900 dark:text-white">
              {fcfa(basket)}
            </span>
          </div>
        </Card>
      </div>
    </div>
  );
}

function ReceiptCard({ data }: { data: ReceiptWidgetData }) {
  return (
    <Card>
      <div className="flex items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#e2f4d8] text-[#2d6914]">
          <Receipt className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="font-heading text-[15px] font-bold text-slate-900 dark:text-white">
            Vente confirmée
          </p>
          <p className="text-xs text-slate-400">Facture {data.invoice_number}</p>
        </div>
        <span className="ml-auto font-mono text-base font-bold text-slate-900 dark:text-white">
          {fcfa(Number(data.total) || 0)}
        </span>
      </div>
    </Card>
  );
}

function PeriodComparisonCard({ data }: { data: PeriodComparisonWidgetData }) {
  const variation = Number(data.variation_percent) || 0;
  const up = variation >= 0;

  return (
    <Card>
      <div className="mb-2.5 flex items-center justify-between">
        <h4 className="font-heading text-[15px] font-bold text-slate-900 dark:text-white">
          Comparaison de périodes
        </h4>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${
            up ? 'bg-[#e2f4d8] text-[#2d6914]' : 'bg-rose-100 text-rose-600'
          }`}
        >
          {up ? <TrendingUp className="size-3" /> : <TrendingDown className="size-3" />}
          {up ? '+' : ''}
          {Math.round(variation)}%
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Kpi
          label={periodLabel(data.period1?.period ?? '')}
          value={fcfa(Number(data.period1?.total) || 0)}
        />
        <Kpi
          label={periodLabel(data.period2?.period ?? '')}
          value={fcfa(Number(data.period2?.total) || 0)}
        />
      </div>
    </Card>
  );
}

function StockAlertsCard({ data }: { data: StockAlertsWidgetData }) {
  const items = Array.isArray(data.items) ? data.items : [];

  // Display stock alert recommendation box matching Image 3 if items present
  const firstItem = items[0] ?? { product_name: 'Riz 5kg', current_stock: 12 };

  return (
    <div className="rounded-2xl bg-[#f8fafc] dark:bg-charcoal-800 p-4 border border-slate-100 dark:border-charcoal-700 flex flex-col gap-3 shadow-2xs">
      <div className="flex items-start gap-2.5 text-slate-800 dark:text-slate-200 text-sm font-medium leading-relaxed">
        <Lightbulb className="size-5 shrink-0 text-amber-500 mt-0.5" />
        <span>
          Votre stock de{' '}
          <strong className="font-bold text-slate-900 dark:text-white">
            {firstItem.product_name}
          </strong>{' '}
          diminue (reste {firstItem.current_stock}). Voulez-vous passer commande ?
        </span>
      </div>

      <div className="flex items-center justify-end gap-2.5 mt-1">
        <button
          type="button"
          className="rounded-full bg-white dark:bg-charcoal-700 border border-slate-200 dark:border-charcoal-600 text-slate-700 dark:text-slate-200 font-semibold text-xs px-5 py-2 hover:bg-slate-50 transition-all active:scale-95 shadow-2xs"
        >
          Ignorer
        </button>
        <button
          type="button"
          className="rounded-full bg-[#76b900] hover:bg-[#68a400] text-white font-bold text-xs px-5 py-2 flex items-center gap-1.5 shadow-sm transition-all active:scale-95"
        >
          <ShoppingCart className="size-3.5" />
          Commander
        </button>
      </div>
    </div>
  );
}

function TopProductsCard({ data }: { data: TopProductsWidgetData }) {
  const items =
    Array.isArray(data.items) && data.items.length > 0
      ? data.items
      : [
          { product_id: '1', product_name: 'Savon liquide', total_quantity: 42 },
          { product_id: '2', product_name: 'Riz 5kg', total_quantity: 28 },
          { product_id: '3', product_name: 'Huile 1L', total_quantity: 15 },
        ];

  const shown = items.slice(0, 3);
  const maxQty = Math.max(...shown.map((item) => Number(item.total_quantity) || 0), 1);
  const barColors = ['bg-[#76b900]', 'bg-[#8cc63f]', 'bg-[#b2d973]'];

  return (
    <Card>
      <h4 className="font-heading text-base font-bold text-slate-900 dark:text-white mb-4">
        Top 3 produits vendus
      </h4>
      <div className="space-y-3.5">
        {shown.map((item, index) => {
          const qty = Number(item.total_quantity) || 0;
          const barColor = barColors[index % barColors.length];
          return (
            <div key={item.product_id ?? index} className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  {item.product_name}
                </span>
                <span className="font-mono text-slate-400 dark:text-slate-400">{qty} unités</span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-slate-100 dark:bg-charcoal-700">
                <div
                  className={`h-full rounded-full ${barColor}`}
                  style={{ width: `${Math.max(8, Math.round((qty / maxQty) * 100))}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function deltaLabel(value: number | null | undefined): string {
  if (value == null) return 'Nouveau';
  if (value === 0) return 'Stable';
  return `${value > 0 ? '+' : ''}${Math.round(value)}%`;
}

function RestaurantPerformanceCard({
  data,
  conversationId,
  messageId,
}: {
  data: RestaurantPerformanceWidgetData;
  conversationId?: string;
  messageId?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const staff = Array.isArray(data.staff) ? data.staff : [];
  const shownStaff = expanded ? staff : staff.slice(0, 4);
  const summary = data.summary;
  const unassigned = data.unattributed;
  const service = data.service_quality?.overall;
  const kitchen = data.kitchen_activity?.totals;
  const previousNet = Number(data.previous_summary?.net_revenue) || 0;
  const netDelta =
    previousNet > 0 ? ((Number(summary.net_revenue) - previousNet) / previousNet) * 100 : null;
  const labels =
    data.attribution === 'seller'
      ? {
          title: 'Performance des vendeurs',
          singular: 'vendeur',
          plural: 'vendeurs',
          metric: 'CA net',
        }
      : data.attribution === 'collector'
        ? {
            title: 'Encaissements par employé',
            singular: 'encaisseur',
            plural: 'encaisseurs',
            metric: 'Montant encaissé',
          }
        : {
            title: 'Performance des serveurs',
            singular: 'serveur',
            plural: 'serveurs',
            metric: 'CA net',
          };
  const canExport =
    Boolean(conversationId && messageId && data.report_id) && !messageId?.startsWith('assistant-');

  async function handleExport(format: 'pdf' | 'xlsx') {
    if (!conversationId || !messageId || !data.report_id || !canExport) {
      toast.error('Le rapport est encore en cours d’enregistrement. Réessayez dans un instant.');
      return;
    }

    setExporting(format);
    try {
      const response = await api.post<{
        file_name: string;
        file_url: string;
        file_type: string;
      }>(`/ai/chat/${conversationId}/messages/${messageId}/reports/${data.report_id}/export`, {
        format,
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.file_url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.download = response.data.file_name;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      toast.success(format === 'pdf' ? 'Rapport PDF généré' : 'Classeur Excel généré');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Impossible de générer le rapport.');
    } finally {
      setExporting(null);
    }
  }

  return (
    <section
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-charcoal-700 dark:bg-charcoal-800"
      aria-labelledby={`restaurant-report-${data.report_id}`}
    >
      <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-charcoal-700 dark:bg-charcoal-900/40 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-lime-100 text-lime-800 dark:bg-lime-400/15 dark:text-lime-300">
              <Users className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Rapport opérationnel
              </p>
              <h4
                id={`restaurant-report-${data.report_id}`}
                className="mt-0.5 text-base font-bold text-slate-900 dark:text-white"
              >
                {labels.title}
              </h4>
              <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                {data.period?.label || 'Journée de service'}
              </p>
            </div>
          </div>
          <span
            className={cn(
              'shrink-0 rounded-full border px-2.5 py-1 text-xs font-bold',
              Number(summary.attribution_rate) >= 80
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
            )}
          >
            {Math.round(Number(summary.attribution_rate) || 0)}% attribué
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
          <div className="col-span-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-charcoal-700 dark:bg-charcoal-800 sm:col-span-1">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">
              {labels.metric}
            </p>
            <p className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
              {fcfa(Number(summary.net_revenue) || 0)}
            </p>
            <p
              className={cn(
                'mt-1 text-xs font-semibold',
                netDelta == null || netDelta >= 0
                  ? 'text-emerald-700 dark:text-emerald-300'
                  : 'text-red-700 dark:text-red-300',
              )}
            >
              {deltaLabel(netDelta)} vs période précédente
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-charcoal-700 dark:bg-charcoal-800">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Ventes</p>
            <p className="mt-1 font-mono text-xl font-bold text-slate-900 dark:text-white">
              {Number(summary.sales_count) || 0}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3 dark:border-charcoal-700 dark:bg-charcoal-800">
            <p className="text-xs font-semibold text-slate-600 dark:text-slate-300">Panier moyen</p>
            <p className="mt-1 font-mono text-base font-bold text-slate-900 dark:text-white">
              {fcfa(Number(summary.average_basket) || 0)}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <h5 className="text-sm font-bold text-slate-900 dark:text-white">Classement</h5>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Brut {fcfa(Number(summary.gross_revenue) || 0)} · Retours{' '}
            {fcfa(Number(summary.refunds) || 0)}
          </p>
        </div>

        {shownStaff.length > 0 ? (
          <ol className="mt-3 divide-y divide-slate-200 dark:divide-charcoal-700">
            {shownStaff.map((line) => {
              const delta = line.delta?.net_revenue_percent;
              return (
                <li
                  key={line.employee_id}
                  className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 py-3"
                >
                  <span className="flex size-7 items-center justify-center rounded-lg bg-slate-100 font-mono text-xs font-bold text-slate-700 dark:bg-charcoal-700 dark:text-slate-200">
                    {line.rank}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                      {line.employee_name}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      {line.sales_count} vente{line.sales_count > 1 ? 's' : ''} · panier{' '}
                      {fcfa(Number(line.average_basket) || 0)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-bold text-slate-900 dark:text-white">
                      {fcfa(Number(line.net_revenue) || 0)}
                    </p>
                    <p
                      className={cn(
                        'text-xs font-semibold',
                        delta == null || delta >= 0
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-red-700 dark:text-red-300',
                      )}
                    >
                      {deltaLabel(delta)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="mt-3 rounded-xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-600 dark:border-charcoal-600 dark:text-slate-300">
            Aucune vente attribuée sur cette période.
          </p>
        )}

        {staff.length > 4 && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="mt-1 flex min-h-11 w-full items-center justify-center gap-1 rounded-lg text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 dark:text-slate-200 dark:hover:bg-charcoal-700"
            aria-expanded={expanded}
          >
            {expanded ? 'Réduire' : `Voir les ${staff.length} ${labels.plural}`}
            <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
          </button>
        )}

        {Number(unassigned?.sales_count) > 0 && (
          <div className="mt-3 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs font-bold">Ventes sans {labels.singular}</p>
              <p className="mt-0.5 text-xs">
                {unassigned.sales_count} vente{unassigned.sales_count > 1 ? 's' : ''} ·{' '}
                {fcfa(Number(unassigned.net_revenue) || 0)} de CA net, conservées séparément.
              </p>
            </div>
          </div>
        )}

        <div className="mt-3 grid grid-cols-3 gap-2" aria-label="Indicateurs opérationnels">
          <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-charcoal-900/50">
            <Clock3 className="size-4 text-sky-700 dark:text-sky-300" aria-hidden />
            <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Service moyen
            </p>
            <p className="font-mono text-xs font-bold text-slate-900 dark:text-white">
              {Number(service?.total_service_minutes) || 0} min
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-charcoal-900/50">
            <Utensils className="size-4 text-sky-700 dark:text-sky-300" aria-hidden />
            <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Tables suivies
            </p>
            <p className="font-mono text-xs font-bold text-slate-900 dark:text-white">
              {data.tables?.length || 0}
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 p-2.5 dark:bg-charcoal-900/50">
            <Receipt className="size-4 text-sky-700 dark:text-sky-300" aria-hidden />
            <p className="mt-1 text-xs font-semibold text-slate-600 dark:text-slate-300">
              Bons cuisine
            </p>
            <p className="font-mono text-xs font-bold text-slate-900 dark:text-white">
              {Number(kitchen?.dispatches) || 0}
            </p>
          </div>
        </div>

        {data.zero_activity?.length > 0 && (
          <p className="mt-3 text-xs text-slate-600 dark:text-slate-300">
            Sans activité :{' '}
            {data.zero_activity.map((employee) => employee.employee_name).join(', ')}.
          </p>
        )}

        <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4 dark:border-charcoal-700 sm:flex-row">
          <button
            type="button"
            onClick={() => void handleExport('pdf')}
            disabled={exporting !== null}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 text-xs font-bold text-slate-800 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 disabled:opacity-60 dark:border-charcoal-600 dark:bg-charcoal-800 dark:text-slate-100 dark:hover:bg-charcoal-700"
          >
            {exporting === 'pdf' ? (
              <Download className="size-4 animate-pulse" />
            ) : (
              <FileText className="size-4" />
            )}
            Exporter en PDF
          </button>
          <button
            type="button"
            onClick={() => void handleExport('xlsx')}
            disabled={exporting !== null}
            className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-lime-400 px-3 text-xs font-bold text-charcoal-950 transition-colors hover:bg-lime-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-400 focus-visible:ring-offset-2 disabled:opacity-60"
          >
            {exporting === 'xlsx' ? (
              <Download className="size-4 animate-pulse" />
            ) : (
              <FileSpreadsheet className="size-4" />
            )}
            Exporter en Excel
          </button>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dispatcher
// ---------------------------------------------------------------------------

function renderWidget(
  widget: AiWidget,
  index: number,
  conversationId?: string,
  messageId?: string,
) {
  switch (widget.type) {
    case 'shop_health':
      return <ShopHealthCard key={index} data={widget.data} />;
    case 'sales_summary':
      return <SalesSummaryCard key={index} data={widget.data} />;
    case 'receipt':
      return <ReceiptCard key={index} data={widget.data} />;
    case 'period_comparison':
      return <PeriodComparisonCard key={index} data={widget.data} />;
    case 'stock_alerts':
      return <StockAlertsCard key={index} data={widget.data} />;
    case 'top_products':
      return <TopProductsCard key={index} data={widget.data} />;
    case 'restaurant_performance':
      return (
        <RestaurantPerformanceCard
          key={index}
          data={widget.data}
          conversationId={conversationId}
          messageId={messageId}
        />
      );
    default:
      return null;
  }
}

export function ChatWidgets({
  widgets,
  conversationId,
  messageId,
}: {
  widgets?: AiWidget[] | null;
  conversationId?: string;
  messageId?: string;
}) {
  if (!Array.isArray(widgets) || widgets.length === 0) {
    return null;
  }

  return (
    <div className="mt-3.5 space-y-3">
      {widgets.map((widget, index) => renderWidget(widget, index, conversationId, messageId))}
    </div>
  );
}
